import {
    ALL_LAYERS,
    currentPenColor,
    DEFAULT_IMG_EDIT,
    DEFAULT_NORTH,
    findPenPreset,
    PEN_PRESETS,
    SCENE_VERSION
} from './constants';
import { round } from './geometry';
import { areaFillFromCode, areaFillToCode } from './areaFill';
import type {
    AreaFill,
    Annotation,
    ImageEdit,
    Layer,
    LeaderAnnotation,
    NailAnnotation,
    NorthState,
    SceneData,
    StratumAnnotation,
    Stroke,
    TextAnnotation
} from './types';

export function parseScene(raw: string): SceneData | null {
    try {
        const data = JSON.parse(raw) as SceneData;
        if (!data || typeof data.aspect !== 'number') {
            return null;
        }
        return data;
    } catch {
        return null;
    }
}

/** One Schicht mark of a saved sketch, as the seeding needs to see it. */
export interface SceneStratum {
    /** The mark's id — absent on every sketch saved before ids were kept. */
    id?: string;
    /** What is written in the box. */
    label: string;
}

/**
 * The Schichten drawn on a saved sketch, in the order they were placed — what
 * seeds the Schichten list of the description the sketch hangs on (see
 * features/findings/sketchStrata).
 *
 * Takes the raw scene string: the scene's shape stays inside this module.
 */
export function sceneStrata(raw: string): SceneStratum[] {
    const scene = raw ? parseScene(raw) : null;
    return (scene?.strata ?? [])
        .map((entry) => ({
            ...(entry.i ? { id: entry.i } : {}),
            label: String(entry.t ?? '').trim()
        }))
        .filter((entry) => entry.label !== '');
}

/**
 * The marks of one layer the drawing actually SHOWS: the layer is on and the
 * mark is not hidden. `scene.layers` lists the VISIBLE layers; absent or empty
 * means all of them, as every scene written before the toggles carries (the
 * editor reads it the same way — see visibleLayersFromScene).
 */
export function visibleMarks<T extends { h?: 0 | 1 }>(
    scene: SceneData,
    layer: Layer,
    marks: T[] | undefined
): T[] {
    const layers = scene.layers?.filter((entry) => ALL_LAYERS.includes(entry));
    const shown = !layers || layers.length === 0 || layers.includes(layer);
    if (!shown) {
        return [];
    }
    return (marks ?? []).filter((mark) => mark.h !== 1);
}

/**
 * The pens a scene was drawn with, as PEN_PRESETS ids in that list's order. A
 * pen is a colour AND a line pattern, so the Profilkasten resolves to itself
 * rather than to the Befundkontur it shares an ink with; marks that are not
 * lines carry a colour only, and match the solid pen of that colour. Only a
 * stroke can carry an area fill, which keeps a house-ink nail resolving to the
 * Befundkontur rather than to the Humus.
 *
 * An eraser stroke never counts — it carries whatever pen was in hand. With
 * `shownOnly`, neither does a hidden mark or one on a layer switched off: the
 * Legende names what is on the sheet. Without it they count: hiding a layer
 * tidies the sheet, it does not take the Holzkohle out of the ground.
 */
export function drawnPenIds(
    scene: SceneData,
    { shownOnly }: { shownOnly: boolean }
): string[] {
    const marksOf = <T extends { h?: 0 | 1 }>(
        layer: Layer,
        marks: T[] | undefined
    ): T[] => (shownOnly ? visibleMarks(scene, layer, marks) : (marks ?? []));
    const used = new Set<string>();
    const add = (color?: string, dash?: number[], fill?: AreaFill) => {
        const preset = color ? findPenPreset(color, dash, fill) : undefined;
        if (preset) {
            used.add(preset.id);
        }
    };
    marksOf('drawing', scene.strokes).forEach((stroke) => {
        if (stroke.e !== 1) {
            add(stroke.c, stroke.d, areaFillFromCode(stroke.f));
        }
    });
    marksOf('symbols', scene.nails).forEach((nail) => add(nail.c));
    marksOf('leaders', scene.leaders).forEach((leader) => add(leader.c));
    marksOf('text', scene.texts).forEach((text) => add(text.c));
    marksOf('symbols', scene.strata).forEach((stratum) => add(stratum.c));
    return PEN_PRESETS.filter((preset) => used.has(preset.id)).map(
        (preset) => preset.id
    );
}

/**
 * The pens of a saved sketch, hidden marks included — what seeds the
 * Einschlüsse of the description it hangs on (see
 * features/findings/sketchInclusions).
 */
export function scenePenIds(raw: string): string[] {
    const scene = raw ? parseScene(raw) : null;
    return scene ? drawnPenIds(scene, { shownOnly: false }) : [];
}

/** Read the background adjustments from a (possibly older) scene. */
export function readImgEdit(scene: SceneData | null): ImageEdit {
    const img = scene?.img;
    if (!img) {
        return { ...DEFAULT_IMG_EDIT };
    }
    return {
        rotate: ((img.rot % 360) + 360) % 360,
        flipH: img.fh === 1,
        flipV: img.fv === 1,
        crop: img.crop
            ? {
                  x: img.crop[0],
                  y: img.crop[1],
                  w: img.crop[2],
                  h: img.crop[3],
                  // A four-number crop is a scene from before straightening —
                  // an upright rectangle, which is what angle 0 means.
                  angle: img.crop[4] ?? 0
              }
            : null,
        brightness: img.br,
        contrast: img.co
    };
}

/** The freehand strokes of a reopened scene (ids are minted fresh on load). */
export function strokesFromScene(scene: SceneData | null): Stroke[] {
    if (!scene) {
        return [];
    }
    return scene.strokes.map((stroke) => ({
        id: crypto.randomUUID(),
        color: currentPenColor(stroke.c),
        width: stroke.w,
        // Absent on every scene written before the Stiftart list, and on every
        // solid line since — which is what a missing dash means.
        ...(stroke.d && stroke.d.length > 0 ? { dash: stroke.d } : {}),
        // Absent on every scene written before the area pens, and on every line
        // since — both of which mean the same thing here.
        ...(areaFillFromCode(stroke.f)
            ? { fill: areaFillFromCode(stroke.f) }
            : {}),
        erase: stroke.e === 1,
        points: stroke.p,
        hidden: stroke.h === 1,
        x: stroke.x ?? 0,
        y: stroke.y ?? 0,
        rotation: stroke.r ?? 0,
        scaleX: stroke.sx ?? 1,
        scaleY: stroke.sy ?? 1
    }));
}

/** The nail/leader/text annotations of a reopened scene. */
export function annotationsFromScene(scene: SceneData | null): Annotation[] {
    if (!scene) {
        return [];
    }
    return [
        ...scene.nails.map((nail) => ({
            id: crypto.randomUUID(),
            type: 'nail' as const,
            x: nail.x,
            y: nail.y,
            text: nail.t,
            color: currentPenColor(nail.c),
            hidden: nail.h === 1,
            rotation: nail.r ?? 0,
            scaleX: nail.sx ?? 1,
            scaleY: nail.sy ?? 1
        })),
        ...scene.leaders.map((leader) => ({
            id: crypto.randomUUID(),
            type: 'leader' as const,
            ax: leader.ax,
            ay: leader.ay,
            bx: leader.bx,
            by: leader.by,
            text: leader.t,
            color: currentPenColor(leader.c),
            hidden: leader.h === 1,
            x: leader.x ?? 0,
            y: leader.y ?? 0,
            rotation: leader.r ?? 0,
            scaleX: leader.sx ?? 1,
            scaleY: leader.sy ?? 1,
            labelRotation: leader.lr ?? 0,
            labelMirrored: leader.lm === 1
        })),
        ...(scene.strata ?? []).map((stratum) => ({
            // The one mark whose id outlives the save — see SceneData.strata.
            id: stratum.i || crypto.randomUUID(),
            type: 'stratum' as const,
            x: stratum.x,
            y: stratum.y,
            text: stratum.t,
            color: currentPenColor(stratum.c),
            hidden: stratum.h === 1,
            rotation: stratum.r ?? 0,
            scaleX: stratum.sx ?? 1,
            scaleY: stratum.sy ?? 1
        })),
        ...(scene.texts ?? []).map((text) => ({
            id: crypto.randomUUID(),
            type: 'text' as const,
            x: text.x,
            y: text.y,
            text: text.t,
            color: currentPenColor(text.c),
            hidden: text.h === 1,
            rotation: text.r ?? 0,
            scaleX: text.sx ?? 1,
            scaleY: text.sy ?? 1
        }))
    ];
}

/** Which layers are visible in a reopened scene (defaulting to all). */
export function visibleLayersFromScene(scene: SceneData | null): Layer[] {
    const layers = scene?.layers?.filter((layer) => ALL_LAYERS.includes(layer));
    return layers && layers.length > 0 ? layers : ALL_LAYERS;
}

/** The north arrow of a reopened scene — absent means a hidden default. */
export function northFromScene(scene: SceneData | null): NorthState {
    const north = scene?.n;
    if (!north) {
        return { ...DEFAULT_NORTH };
    }
    return {
        show: true,
        angle: north.a,
        x: north.x,
        y: north.y,
        scale: north.s ?? 1
    };
}

interface SerializeInput {
    aspect: number;
    visibleLayers: Layer[];
    backgroundMediaId?: string;
    imgEdit: ImageEdit;
    lines: Stroke[];
    nails: NailAnnotation[];
    leaders: LeaderAnnotation[];
    texts: TextAnnotation[];
    strata: StratumAnnotation[];
    north: NorthState;
}

/** Flatten the editor state into the compact, resolution-independent scene JSON. */
export function serializeScene(input: SerializeInput): string {
    const { imgEdit } = input;
    const data: SceneData = {
        v: SCENE_VERSION,
        aspect: round(input.aspect, 5),
        layers: input.visibleLayers,
        bg: input.backgroundMediaId ?? '',
        // The per-sketch north arrow — stored only while it is shown (a hidden
        // arrow is simply left out, so this sketch has none on reopen).
        ...(input.north.show
            ? {
                  n: {
                      a: round(input.north.angle, 1),
                      x: round(input.north.x, 2),
                      y: round(input.north.y, 2),
                      ...(input.north.scale !== 1
                          ? { s: round(input.north.scale, 3) }
                          : {})
                  }
              }
            : {}),
        img: {
            rot: imgEdit.rotate,
            fh: imgEdit.flipH ? 1 : 0,
            fv: imgEdit.flipV ? 1 : 0,
            // The angle is only written once there is one, so an unstraightened
            // crop keeps producing exactly the scene it always did.
            crop: imgEdit.crop
                ? imgEdit.crop.angle
                    ? [
                          round(imgEdit.crop.x, 4),
                          round(imgEdit.crop.y, 4),
                          round(imgEdit.crop.w, 4),
                          round(imgEdit.crop.h, 4),
                          round(imgEdit.crop.angle, 3)
                      ]
                    : [
                          round(imgEdit.crop.x, 4),
                          round(imgEdit.crop.y, 4),
                          round(imgEdit.crop.w, 4),
                          round(imgEdit.crop.h, 4)
                      ]
                : null,
            br: round(imgEdit.brightness, 3),
            co: round(imgEdit.contrast, 1)
        },
        strokes: input.lines.map((line) => ({
            c: line.color,
            w: round(line.width, 5),
            e: line.erase ? 1 : 0,
            p: line.points.map((value) => round(value, 4)),
            ...(line.dash && line.dash.length > 0
                ? { d: line.dash.map((value) => round(value, 3)) }
                : {}),
            ...(line.fill ? { f: areaFillToCode(line.fill) } : {}),
            ...(line.hidden ? { h: 1 as const } : {}),
            ...(line.x ? { x: round(line.x, 4) } : {}),
            ...(line.y ? { y: round(line.y, 4) } : {}),
            ...(line.rotation ? { r: round(line.rotation, 1) } : {}),
            ...(line.scaleX && line.scaleX !== 1
                ? { sx: round(line.scaleX, 3) }
                : {}),
            ...(line.scaleY && line.scaleY !== 1
                ? { sy: round(line.scaleY, 3) }
                : {})
        })),
        nails: input.nails.map((nail) => ({
            x: round(nail.x, 2),
            y: round(nail.y, 2),
            t: nail.text,
            ...(nail.color ? { c: nail.color } : {}),
            ...(nail.hidden ? { h: 1 as const } : {}),
            ...(nail.rotation ? { r: round(nail.rotation, 1) } : {}),
            ...(nail.scaleX && nail.scaleX !== 1
                ? { sx: round(nail.scaleX, 3) }
                : {}),
            ...(nail.scaleY && nail.scaleY !== 1
                ? { sy: round(nail.scaleY, 3) }
                : {})
        })),
        // A Schicht with nothing written in it is an empty box; it is dropped on
        // save the same way an empty free text is.
        strata: input.strata
            .filter((item) => item.text.trim() !== '')
            .map((item) => ({
                x: round(item.x, 2),
                y: round(item.y, 2),
                t: item.text,
                i: item.id,
                ...(item.color ? { c: item.color } : {}),
                ...(item.hidden ? { h: 1 as const } : {}),
                ...(item.rotation ? { r: round(item.rotation, 1) } : {}),
                ...(item.scaleX && item.scaleX !== 1
                    ? { sx: round(item.scaleX, 3) }
                    : {}),
                ...(item.scaleY && item.scaleY !== 1
                    ? { sy: round(item.scaleY, 3) }
                    : {})
            })),
        texts: input.texts
            .filter((item) => item.text.trim() !== '')
            .map((item) => ({
                x: round(item.x, 2),
                y: round(item.y, 2),
                t: item.text,
                ...(item.color ? { c: item.color } : {}),
                ...(item.hidden ? { h: 1 as const } : {}),
                ...(item.rotation ? { r: round(item.rotation, 1) } : {}),
                ...(item.scaleX && item.scaleX !== 1
                    ? { sx: round(item.scaleX, 3) }
                    : {}),
                ...(item.scaleY && item.scaleY !== 1
                    ? { sy: round(item.scaleY, 3) }
                    : {})
            })),
        leaders: input.leaders.map((leader) => ({
            ax: round(leader.ax, 2),
            ay: round(leader.ay, 2),
            bx: round(leader.bx, 2),
            by: round(leader.by, 2),
            t: leader.text,
            ...(leader.color ? { c: leader.color } : {}),
            ...(leader.hidden ? { h: 1 as const } : {}),
            ...(leader.x ? { x: round(leader.x, 4) } : {}),
            ...(leader.y ? { y: round(leader.y, 4) } : {}),
            ...(leader.rotation ? { r: round(leader.rotation, 1) } : {}),
            ...(leader.scaleX && leader.scaleX !== 1
                ? { sx: round(leader.scaleX, 3) }
                : {}),
            ...(leader.scaleY && leader.scaleY !== 1
                ? { sy: round(leader.scaleY, 3) }
                : {}),
            ...(leader.labelRotation
                ? { lr: round(leader.labelRotation, 1) }
                : {}),
            ...(leader.labelMirrored ? { lm: 1 as const } : {})
        }))
    };
    return JSON.stringify(data);
}
